import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import CheckMarkIcon from '../../assets/icons/checkmark.svg?react';
import ViewIcon from '../../assets/icons/view.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import LocalDate from '../../components/common/local-date.tsx';
import Modal from '../../components/common/modal.tsx';
import Tooltip from '../../components/common/tooltip.tsx';
import QuestionPreview from '../../components/questions/question-content/question-preview.tsx';
import copyTextToClipboard from '../../utils/copy-text-to-clipboard.ts';
import formatApiMessage from '../../utils/format-api-message.ts';
import { useGetMe } from '../../utils/hooks.ts';
import { RoutesPermissionEnum as RP } from '../../utils/enums.ts';

type ImportResultGroup = {
  label: 'Draft mode' | 'Published' | 'Updated' | 'Skipped' | 'Error';
  entries: string[];
};

type PreviewRow = {
  rowNumber: number;
  questionId?: number;
  questionDbId?: number;
  sourceQuestionId?: number;
  sourceRelationType?: string;
  contentPreview: string;
  change: string;
  action: 'create' | 'update' | 'skip' | 'error';
  result?: 'changed' | 'skipped' | 'error';
  resultMessage?: string;
};

type ImportUploadSummary = {
  phase: 'Staged' | 'Applied';
  previewRows: PreviewRow[];
  updated: Array<{
    rowNumber: number;
    originalId: number;
    reason?: string;
  }>;
  skipped: Array<{ rowNumber: number; reference: string; reason: string }>;
  errors: Array<{ rowNumber: number; reference: string; messages: unknown[] }>;
  resultGroups: ImportResultGroup[];
  clipboardText: string;
  appliedAt?: string;
};

type ImportUpload = {
  id: number;
  fileName: string;
  fileUrl?: string;
  worksheetName?: string;
  totalRows: number;
  insertedCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  createdAt: string;
  summary: ImportUploadSummary;
};

const isImportUpload = (data: unknown): data is ImportUpload =>
  !!data &&
  typeof data === 'object' &&
  'id' in data &&
  'summary' in data;

const getPreviewRowClassName = (action: PreviewRow['action']) => {
  switch (action) {
    case 'create':
      return 'text-green';
    case 'update':
      return 'text-[#b54708]';
    case 'error':
      return 'text-[#b42318]';
    case 'skip':
    default:
      return 'text-dark-gray';
  }
};

const getPreviewRowChangeText = (row: PreviewRow, isApplied: boolean) => {
  const changeText = isApplied && row.resultMessage ? row.resultMessage : row.change;
  const isErrorRow =
    row.action === 'error' || (isApplied && row.result === 'error');

  if (!isErrorRow) {
    return changeText;
  }

  return changeText.startsWith('Error: ') ? changeText : `Error: ${changeText}`;
};

const getPreviewRowResult = (row: PreviewRow, isApplied: boolean) => {
  if (!isApplied) {
    return (
      <span className="text-[11px] font-medium uppercase tracking-wide text-dark-gray/70">
        Pending
      </span>
    );
  }

  if (row.result === 'changed') {
    return (
      <span className="inline-flex items-center gap-1 font-semibold text-green">
        <CheckMarkIcon className="h-3.5 w-3.5" />
        Changed
      </span>
    );
  }

  if (row.result === 'error') {
    return <span className="font-semibold text-[#b42318]">Error</span>;
  }

  return <span className="font-medium text-dark-gray">Skipped</span>;
};

const getWorkbookErrorMessage = (
  errors: Array<{ rowNumber: number; reference: string; messages: unknown[] }>,
) => {
  const workbookError = errors.find((item) => item.rowNumber === 0);

  if (!workbookError?.messages?.length) {
    return '';
  }

  return formatApiMessage(workbookError.messages);
};

const DataUploadReview = () => {
  const { uploadId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin, isVA, isTeacher } = useGetMe();
  const [upload, setUpload] = useState<ImportUpload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');
  const [previewQuestionId, setPreviewQuestionId] = useState<number>();

  const loadUpload = useCallback(async () => {
    if (!uploadId) {
      setError('Upload was not found.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/question/import-uploads/${uploadId}`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      const data = (await response.json().catch(() => null)) as
        | ImportUpload
        | { message?: unknown }
        | null;

      if (!response.ok || !isImportUpload(data)) {
        const message =
          data &&
          !Array.isArray(data) &&
          typeof data === 'object' &&
          'message' in data &&
          data.message
            ? formatApiMessage(data.message)
            : 'Unable to load this upload.';
        throw new Error(message);
      }

      setUpload(data);
    } catch (loadError) {
      const message =
        loadError instanceof Error
          ? loadError.message
          : 'Unable to load this upload.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, uploadId]);

  useEffect(() => {
    loadUpload();
  }, [loadUpload]);

  const applyUpload = async () => {
    if (!uploadId) {
      return;
    }

    setIsApplying(true);

    const applyPromise = (async () => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/question/import-uploads/${uploadId}/apply`,
        {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      );
      const data = (await response.json().catch(() => null)) as
        | ImportUpload
        | { message?: unknown }
        | null;

      if (!response.ok || !isImportUpload(data)) {
        const message =
          data &&
          !Array.isArray(data) &&
          typeof data === 'object' &&
          'message' in data &&
          data.message
            ? formatApiMessage(data.message)
            : 'Unable to apply this staged upload.';
        throw new Error(message);
      }

      setUpload(data);
      return data;
    })().finally(() => {
      setIsApplying(false);
    });

    toast.promise(applyPromise, {
      pending: 'Applying staged upload...',
      success: 'Staged upload applied',
      error: {
        render: ({ data }) =>
          data instanceof Error ? data.message : 'Apply failed',
      },
    });
  };

  if (isLoading) {
    return (
      <section className="rounded-lg border border-table-border bg-white p-6">
        <p className="text-sm text-dark-gray">Loading upload review...</p>
      </section>
    );
  }

  if (error || !upload) {
    return (
      <section className="rounded-lg border border-[#f5c2c7] bg-[#fff5f5] p-6">
        <p className="text-sm text-[#b42318]">
          {error || 'Upload was not found.'}
        </p>
      </section>
    );
  }

  const isStaged = upload.summary.phase === 'Staged';
  const clipboardText = upload.summary.clipboardText;
  const canEditQuestion = isAdmin || isVA || isTeacher;
  const workbookErrorMessage = getWorkbookErrorMessage(upload.summary.errors);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <section className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1>Upload Review</h1>
          <p className="description">
            Review every proposed change before applying this upload.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            className="text-sm font-medium text-blue underline-offset-2 hover:underline"
            to={RP.DATA_UPLOAD_HISTORY}
          >
            Back to Upload History
          </Link>
          <Link
            className="text-sm font-medium text-blue underline-offset-2 hover:underline"
            to={RP.DATA_UPLOAD}
          >
            Back to Upload
          </Link>
        </div>
      </section>

      <section className="rounded-lg border border-table-border bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-dark-blue">
              <LocalDate date={upload.createdAt} format="yyyy/MM/dd '|' p" />
            </h2>
            <p className="mt-1 text-sm text-dark-gray">{upload.fileName}</p>
            {upload.worksheetName && (
              <p className="mt-1 text-sm text-dark-gray">
                Sheet: {upload.worksheetName}
              </p>
            )}
            <p className="mt-2 text-sm text-dark-blue">
              Status: {upload.summary.phase}
            </p>
            {!!upload.summary.appliedAt && (
              <p className="mt-1 text-sm text-dark-gray">
                Applied on{' '}
                <LocalDate date={upload.summary.appliedAt} format="yyyy/MM/dd '|' p" />
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {upload.fileUrl && (
              <a
                className="link-blue-button"
                href={upload.fileUrl}
                rel="noreferrer"
                target="_blank"
              >
                Workbook
              </a>
            )}
            {!isStaged && !!clipboardText && (
              <button
                className="rounded-lg border border-blue px-4 py-2 text-sm font-medium text-blue"
                onClick={() => copyTextToClipboard(clipboardText)}
                type="button"
              >
                Copy IDs
              </button>
            )}
            {isStaged && (
              <button
                className="rounded-lg bg-blue px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-disabled"
                disabled={isApplying}
                onClick={applyUpload}
                type="button"
              >
                {isApplying ? 'Applying...' : 'Apply Upload'}
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm text-dark-gray">
          <span>{upload.totalRows} rows</span>
          <span>{upload.insertedCount} {isStaged ? 'would add' : 'added'}</span>
          <span>{upload.updatedCount} {isStaged ? 'would update' : 'updated'}</span>
          <span>{upload.skippedCount} {isStaged ? 'would skip' : 'skipped'}</span>
          <span>{upload.errorCount} {isStaged ? 'would error' : 'errors'}</span>
        </div>

        {!!workbookErrorMessage && (
          <div className="mt-4 rounded-lg border border-[#f5c2c7] bg-[#fff5f5] p-4">
            <p className="text-sm font-medium text-[#b42318]">
              {workbookErrorMessage}
            </p>
          </div>
        )}
      </section>

      <section className="rounded-lg border border-table-border bg-white p-6">
        <h2 className="text-lg font-semibold text-dark-blue">Row Preview</h2>
        <p className="mt-2 text-sm text-dark-gray">
          {isStaged
            ? 'Valid new-question rows are already created as drafts here so you can preview and edit them before final apply.'
            : 'Each row shows what happened when this upload was applied.'}
        </p>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full table-fixed text-xs text-dark-gray">
            <thead>
              <tr className="border-b border-table-border text-left text-[11px] uppercase tracking-wide text-dark-blue">
                <th className="w-16 py-2 pr-3">Row</th>
                <th className="w-20 py-2 pr-3">QID</th>
                <th className="w-24 py-2 pr-3">QSourceID</th>
                <th className="w-36 py-2 pr-3">QSourceRelation</th>
                <th className="py-2 pr-3">Question Preview</th>
                <th className="w-48 py-2 pr-3">Change</th>
                <th className="w-24 py-2 pr-3">Actions</th>
                <th className="w-24 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {upload.summary.previewRows.map((row) => {
                const isAppliedCreatedRow =
                  !isStaged && row.action === 'create' && row.result === 'changed';

                return (
                  <tr
                    className={`border-b border-light-gray/60 align-top ${getPreviewRowClassName(
                      row.action,
                    )} ${isAppliedCreatedRow ? 'font-semibold' : ''}`}
                    key={`preview-row-${row.rowNumber}`}
                  >
                    <td className="py-2 pr-3">{row.rowNumber}</td>
                    <td className="py-2 pr-3">{row.questionId || 'none'}</td>
                    <td className="py-2 pr-3">{row.sourceQuestionId || 'none'}</td>
                    <td className="truncate py-2 pr-3">
                      {row.sourceRelationType || 'none'}
                    </td>
                    <td className="truncate py-2 pr-3">
                      {row.contentPreview || '—'}
                    </td>
                    <td className="truncate py-2 pr-3">
                      {getPreviewRowChangeText(row, !isStaged)}
                    </td>
                    <td className="py-2 pr-3">
                      {row.questionDbId ? (
                        <div className="flex items-center gap-1">
                          <Tooltip tip={'Preview'}>
                            <button
                              className="cursor-pointer p-1"
                              onClick={() => setPreviewQuestionId(row.questionDbId)}
                              type="button"
                            >
                              <ViewIcon />
                            </button>
                          </Tooltip>
                          {canEditQuestion && (
                            <Tooltip tip={'Edit'}>
                              <Link
                                className="cursor-pointer p-1"
                                to={RP.EDIT_QUESTION_QUESTION_ID.replace(
                                  ':questionId',
                                  String(row.questionDbId),
                                )}
                              >
                                <EditIcon />
                              </Link>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2">{getPreviewRowResult(row, !isStaged)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {!isStaged && (
        <section className="rounded-lg border border-table-border bg-white p-6">
          <h2 className="text-lg font-semibold text-dark-blue">Apply Result</h2>

          {!!upload.summary.resultGroups?.length && (
            <div className="mt-4 grid gap-6 lg:grid-cols-5">
              {upload.summary.resultGroups.map((group) => (
                <div key={group.label}>
                  <p className="text-sm font-semibold text-dark-blue">
                    {group.label}
                  </p>
                  {group.entries.length ? (
                    <ul className="mt-2 space-y-1 text-sm text-dark-gray">
                      {group.entries.map((entry, index) => (
                        <li key={`${group.label}-${entry}-${index}`}>{entry}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-dark-gray">None</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {!!upload.summary.updated?.length && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-dark-blue">Updated</p>
              <ul className="mt-2 space-y-2 text-sm text-dark-gray">
                {upload.summary.updated.map((item) => (
                  <li key={`updated-${item.rowNumber}-${item.originalId}`}>
                    {item.originalId}: {item.reason || 'Updated existing question'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!upload.summary.skipped?.length && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-dark-blue">Skipped</p>
              <ul className="mt-2 space-y-2 text-sm text-dark-gray">
                {upload.summary.skipped.map((item) => (
                  <li key={`skipped-${item.rowNumber}-${item.reference}`}>
                    {item.reference}: {item.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!!upload.summary.errors?.length && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-dark-blue">Errors</p>
              <ul className="mt-2 space-y-2 text-sm text-dark-gray">
                {upload.summary.errors.map((item) => (
                  <li key={`error-${item.rowNumber}-${item.reference}`}>
                    {item.reference}: {formatApiMessage(item.messages)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <Modal
        fullScreen
        isOpen={!!previewQuestionId}
        onClose={() => setPreviewQuestionId(undefined)}
        title={'Question preview'}
      >
        {previewQuestionId && <QuestionPreview questionId={previewQuestionId} />}
      </Modal>
    </div>
  );
};

export default DataUploadReview;
